import React, { useState, useEffect, useRef } from "react";
import { loadStripe, StripeElementsOptions } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import {
  PlxsAlert,
  PlxsText,
  PlxsTextLink,
  PlxsCallout,
} from "@plexlogic/plexus-ui";
import { DrawData } from "../../types/DrawData";
import { WizardStepHeader } from "../index";
import WizardFooterNav from "../WizardFooterNav";
import apiService from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import { pendoService } from "../../services/pendo";

interface PaymentStepProps {
  drawData: DrawData;
  onNext: (updatedDrawData?: Partial<DrawData>) => void;
  onBack: () => void;
  drawStatus?: string;
}

// Load Stripe with publishable key from environment
const stripePromise = loadStripe(
  process.env.REACT_APP_DRAWS_STRIPE_PUBLISHABLE_KEY || ""
);

// Get draw price from environment (in cents, e.g., 50 for $0.50)
const DRAW_PRICE_CENTS = parseInt(
  process.env.REACT_APP_DRAW_PRICE_AUD || "50",
  10
);

const PaymentForm: React.FC<PaymentStepProps> = ({
  drawData,
  onNext,
  onBack,
  drawStatus,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { isAuthenticated, user } = useAuth();

  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [paymentIntentId, setPaymentIntentId] = useState<string>(
    drawData.paymentIntentId || ""
  );
  const [clientSecret, setClientSecret] = useState<string>("");
  const [paymentStatus, setPaymentStatus] = useState<string>(
    drawData.paymentStatus || "unpaid"
  );
  const [polling, setPolling] = useState(false);
  const hasCreatedPaymentIntent = useRef(false);

  // Create PaymentIntent when component mounts
  useEffect(() => {
    if (!drawData.drawId) {
      setError("No draw ID found. Please go back and create a draw first.");
      return;
    }

    // If we already have a payment intent and it's paid, skip creation
    if (paymentIntentId && paymentStatus === "paid") {
      return;
    }

    // Prevent duplicate calls
    if (hasCreatedPaymentIntent.current) {
      return;
    }

    // Create payment intent
    const createPaymentIntent = async () => {
      try {
        hasCreatedPaymentIntent.current = true;
        setLoading(true);
        setError("");

        const response = await apiService.createPaymentIntent({
          draw_id: drawData.drawId!,
        });

        setPaymentIntentId(response.payment_intent_id);
        setClientSecret(response.client_secret);
        setPaymentStatus(response.status);
      } catch (err: any) {
        hasCreatedPaymentIntent.current = false; // Reset on error so user can retry
        const detail =
          (typeof err?.message === "string" && err.message) ||
          err?.response?.data?.detail ||
          "";
        if (
          typeof detail === "string" &&
          /payment already completed/i.test(detail)
        ) {
          setPaymentStatus("paid");
          setError("");
          return;
        }
        setError(detail || "Failed to initialize payment. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    createPaymentIntent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawData.drawId]); // Only run when drawId changes

  // Poll for payment status after confirmation
  const pollPaymentStatus = async (intentId: string): Promise<boolean> => {
    setPolling(true);
    const maxAttempts = 20; // Poll for up to 20 seconds

    for (let attempts = 0; attempts < maxAttempts; attempts++) {
      try {
        // Force a live Stripe lookup only on the first attempt; it's
        // authoritative immediately, so later attempts can use cheap DB reads.
        const response = await apiService.getPaymentStatus(intentId, attempts === 0);

        if (response.status === "succeeded") {
          pendoService.trackEvent("draw_payment_succeeded", {
            payment_intent_id: intentId,
            funding: response.funding,
            card_brand: response.card_brand,
            last4: response.last4,
            amount: response.amount,
            currency: response.currency,
            draw_id: drawData.drawId,
            user_email: user?.email,
            user_name: user?.name,
            organisation: user?.rootOrganisationName,
            organisation_id: user?.rootOrganisationId,
          });
          setPaymentStatus("paid");
          setPolling(false);
          setError("");
          return true;
        } else if (
          response.status === "failed" ||
          response.status === "canceled"
        ) {
          setPaymentStatus(response.status);
          setPolling(false);
          setError("Payment was not successful. Please try again.");
          return false;
        }

        // Wait 1 second before next attempt (if not last attempt)
        if (attempts < maxAttempts - 1) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
      } catch (err) {
        // On error, continue polling unless it's the last attempt
        if (attempts === maxAttempts - 1) {
          setPolling(false);
          setError("Failed to verify payment. Please contact support.");
          return false;
        }
        // Wait 1 second before retrying
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    }

    // Max attempts reached without success
    setPolling(false);
    setError(
      "Payment verification is taking longer than expected. Please refresh the page."
    );
    return false;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements || !paymentIntentId || !clientSecret) {
      setError("Payment not initialized. Please refresh the page.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error("Card element not found");
      }

      // Confirm payment with Stripe using the client secret we got when creating the intent
      const { error: stripeError } =
        await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: cardElement,
          },
        });

      if (stripeError) {
        const stalePi = (stripeError as any).payment_intent;
        if (
          stripeError.code === "payment_intent_unexpected_state" &&
          stalePi?.status === "succeeded"
        ) {
          // Already succeeded (e.g. a retry after a prior poll timed out) — still
          // route through pollPaymentStatus so the Pendo event fires and
          // funding/card details are read, same as the primary success path.
          await pollPaymentStatus(paymentIntentId);
          setLoading(false);
          return;
        }
        setError(stripeError.message || "Payment failed. Please try again.");
        setLoading(false);
        return;
      }

      // Confirm with our backend (even on immediate success with no 3D Secure)
      // so the payment record — status, card details, funding — is
      // read/persisted before we treat the payment as complete.
      await pollPaymentStatus(paymentIntentId);
    } catch (err: any) {
      setError(
        err.response?.data?.detail ||
          err.message ||
          "Payment failed. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentClick = () => {
    const mockEvent = {
      preventDefault: () => {},
    } as React.FormEvent;
    // handleSubmit owns its own error/loading handling; fire-and-forget.
    void handleSubmit(mockEvent);
  };

  const handleNext = () => {
    if (paymentStatus !== "paid") {
      setError("Please complete payment before proceeding.");
      return;
    }

    onNext({
      paymentIntentId,
      paymentStatus: "paid",
    });
  };

  const cardElementOptions = {
    hidePostalCode: true,
    style: {
      base: {
        fontSize: "16px",
        color: "#424770",
        "::placeholder": {
          color: "#aab7c4",
        },
      },
      invalid: {
        color: "#9e2146",
      },
    },
  };

  const isScheduledNotRun = drawStatus === "scheduled";

  return (
    <div>
      <WizardStepHeader
        title={
          isScheduledNotRun
            ? "Ready to lock in your winners?"
            : "Ready to reveal your winners?"
        }
        description={
          isScheduledNotRun
            ? "Pay now so your winners are ready to view as soon as the draw runs"
            : "Your draw has run – pay to see who won."
        }
      />

      <div style={{ marginTop: "24px" }}>
        <div
          style={{
            backgroundColor: "var(--plexus-color-surface-50)",
            borderRadius: "var(--plexus-space-xs)",
            padding: "var(--plexus-space-xl)",
            marginBottom: "var(--plexus-space-xl)",
          }}
        >
          {isAuthenticated ? (
            isScheduledNotRun ? (
              <PlxsText size="sm">
                A payment of{" "}
                <strong>${(DRAW_PRICE_CENTS / 100).toFixed(2)} AUD</strong> is
                required so your winners are viewable as soon as the draw
                runs at its scheduled time.
              </PlxsText>
            ) : (
              <PlxsText size="sm">
                A payment of{" "}
                <strong>${(DRAW_PRICE_CENTS / 100).toFixed(2)} AUD</strong>{" "}
                unlocks your winners.
              </PlxsText>
            )
          ) : isScheduledNotRun ? (
            <PlxsText size="sm">
              <strong>Payment required to view your winners.</strong>{" "}
              Pay now so your results are ready as soon as the draw runs.
              View and save them from your free{" "}
              <PlxsTextLink href="https://www.plexus.co" target="_blank">
                Plexus
              </PlxsTextLink>{" "}
              account.
            </PlxsText>
          ) : (
            <PlxsText size="sm">
              <strong>Payment required to reveal your winners.</strong>{" "}
              Your draw has already run. Pay now to view and save your results
              from your free{" "}
              <PlxsTextLink href="https://www.plexus.co" target="_blank">
                Plexus
              </PlxsTextLink>{" "}
              account.
            </PlxsText>
          )}

          <div
            style={{
              borderTop: "1px solid var(--plexus-color-neutral-600)",
              margin: "20px 0",
            }}
          />

          {error && (
            <div style={{ marginBottom: "16px" }}>
              <PlxsAlert variant="error">{error}</PlxsAlert>
            </div>
          )}

          {paymentStatus === "paid" && (
            <div style={{ marginBottom: "16px" }}>
              <PlxsCallout
                dataTestId="payment-success-callout"
                tone="positive"
                message="Payment successful! Your winners are ready to view."
              />
            </div>
          )}

          {paymentStatus !== "paid" && (
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: "24px" }}>
                <PlxsText size="sm">Enter your card details:</PlxsText>
                <div
                  style={{
                    padding: "12px",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    backgroundColor: "#fff",
                    marginTop: "8px",
                  }}
                >
                  <CardElement options={cardElementOptions} />
                </div>
              </div>
            </form>
          )}
        </div>
      </div>

      <WizardFooterNav
        backButton={{
          dataTestId: "payment-back-button",
          label: "Back",
          variant: "secondary",
          tone: "neutral",
          onClick: onBack,
        }}
        nextButton={
          paymentStatus === "paid"
            ? {
                dataTestId: "payment-next-button",
                label:
                  drawStatus === "scheduled"
                    ? "Continue"
                    : "Reveal my winners",
                variant: "primary",
                tone: "positive",
                onClick: handleNext,
                isDisabled: false,
              }
            : {
                dataTestId: "draw-wizard-pay-now-button",
                label:
                  loading || polling
                    ? polling
                      ? "Verifying payment..."
                      : "Processing payment..."
                    : "Pay to reveal winners",
                variant: "primary",
                tone: "positive",
                onClick: handlePaymentClick,
                isDisabled: !stripe || loading || polling,
                isLoading: loading || polling,
              }
        }
      />
    </div>
  );
};

// Wrapper component with Stripe Elements provider
const PaymentStep: React.FC<PaymentStepProps> = (props) => {
  const elementsOptions: StripeElementsOptions = {
    mode: "payment",
    amount: DRAW_PRICE_CENTS, // Amount in cents
    currency: "aud",
  };

  return (
    <Elements stripe={stripePromise} options={elementsOptions}>
      <PaymentForm {...props} />
    </Elements>
  );
};

export default PaymentStep;
